/**
 * How the free period is named, by its length (Seven Free Days journey rulings,
 * 2026-09-27, section G — docs/plans/SFD_JOURNEY_RULINGS_2026_09_27.md).
 *
 * One free period: it starts when the first plan is built. It is 7 days by default,
 * 14 or 30 with a partner code, or a month with a free-month code (J21). Every surface
 * names it by that length, with ONE vocabulary:
 *
 *   7       "free week"      / "7 free days"
 *   14      "2 free weeks"   / "14 free days"
 *   30, 31  "free month"     (both forms)
 *   N       "N free days"    (both forms)
 *
 * The backend has the same helper (services/free_period_naming.py). Both are pinned by
 * ONE table of cases (utils/__fixtures__/freePeriodNaming.cases.json), so the two
 * vocabularies cannot drift. Pure, no React; never throws. A length that is not a
 * positive whole number reads as 7.
 */

export const DEFAULT_FREE_PERIOD_DAYS = 7;

/** A usable length: a positive whole number of days, else 7 (a string, a boolean, 7.5 or 0 read as 7). */
export function normalizeFreePeriodDays(days: unknown): number {
  if (typeof days !== 'number' || !Number.isInteger(days) || days <= 0) return DEFAULT_FREE_PERIOD_DAYS;
  return days;
}

export function isFreeMonth(days: unknown): boolean {
  const n = normalizeFreePeriodDays(days);
  return n === 30 || n === 31;
}

/** Whether the period's name takes a plural verb ("2 free weeks end"). */
function isPlural(days: unknown): boolean {
  const n = normalizeFreePeriodDays(days);
  return n !== DEFAULT_FREE_PERIOD_DAYS && !isFreeMonth(n);
}

/** "free week" / "2 free weeks" / "free month" / "N free days". */
export function freePeriodName(days: unknown): string {
  const n = normalizeFreePeriodDays(days);
  if (n === 7) return 'free week';
  if (n === 14) return '2 free weeks';
  if (isFreeMonth(n)) return 'free month';
  return `${n} free days`;
}

/** "7 free days" / "14 free days" / "free month" / "N free days". */
export function freeDaysPhrase(days: unknown): string {
  const n = normalizeFreePeriodDays(days);
  return isFreeMonth(n) ? 'free month' : `${n} free days`;
}

/** "7-day free trial" / "14-day free trial" / "free month" / "N-day free trial". */
export function freeTrialLabel(days: unknown): string {
  const n = normalizeFreePeriodDays(days);
  return isFreeMonth(n) ? 'free month' : `${n}-day free trial`;
}

/** J11: "Your 7 free days start now" / "Your free month starts now". */
export function startCardTitle(days: unknown): string {
  const n = normalizeFreePeriodDays(days);
  return isFreeMonth(n) ? 'Your free month starts now' : `Your ${n} free days start now`;
}

/** The Subscription drawer during the period: "Your 7 free days with Coach Cedar". */
export function freePeriodDrawerTitle(days: unknown): string {
  const n = normalizeFreePeriodDays(days);
  return isFreeMonth(n) ? 'Your free month with Coach Cedar' : `Your ${n} free days with Coach Cedar`;
}

/** The period is over: "Your 7 days with Coach Cedar are done" / "Your free month with Coach Cedar is done". */
export function freePeriodDone(days: unknown): string {
  const n = normalizeFreePeriodDays(days);
  return isFreeMonth(n) ? 'Your free month with Coach Cedar is done' : `Your ${n} days with Coach Cedar are done`;
}

/** J16: "your free week ends tomorrow at midnight" / "your 2 free weeks end …". */
export function freePeriodEndsTomorrow(days: unknown): string {
  return `your ${freePeriodName(days)} ${isPlural(days) ? 'end' : 'ends'} tomorrow at midnight`;
}

/** J11: "Your free week runs until Tue, Oct 6" / "Your 2 free weeks run until …". */
export function freePeriodRunsUntil(days: unknown, dateText: string): string {
  return `Your ${freePeriodName(days)} ${isPlural(days) ? 'run' : 'runs'} until ${dateText}`;
}

/**
 * J13, Account: "Free week · 5 days left" / "Free week · 1 day left" / "Free week · last day"
 * (0 days after today). null → the name alone.
 */
export function freePeriodAccountLabel(days: unknown, daysLeft: number | null | undefined): string {
  const name = freePeriodName(days);
  const cap = name.charAt(0).toUpperCase() + name.slice(1);
  if (typeof daysLeft !== 'number' || !Number.isFinite(daysLeft)) return cap;
  const left = Math.floor(daysLeft);
  if (left <= 0) return `${cap} · last day`;
  return `${cap} · ${left} day${left === 1 ? '' : 's'} left`;
}

/** The drawer's progress bar: one step per day up to 14 days; null (a continuous bar) beyond. */
export function freePeriodProgressSteps(days: unknown): number | null {
  const n = normalizeFreePeriodDays(days);
  return n <= 14 ? n : null;
}

/** A date the way the free-period copy prints it: "Tue, Oct 6" (the phone's calendar). */
export function freePeriodDateText(d: Date | null | undefined): string | null {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}
